import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import Auth from "../services/AuthService";
import util from "../utils/util";
import useActivityLogger from '../pages/useActivityLogger';

// ─── IMS feature highlights shown on the left panel ──────────────────────────
const IMS_MODULES = [
    { code:'FLEET',  title:'Fleet & Vehicle Management',              color:'#0ea5e9' },
    { code:'ASSETS', title:'Medical & Non-Medical Assets',            color:'#8b5cf6' },
    { code:'INVENT', title:'Inventory & Spare Parts',                 color:'#f59e0b' },
    { code:'MAINT',  title:'Maintenance & Service Requests',          color:'#10b981' },
    { code:'REPORT', title:'Reports & Analytics Dashboards',          color:'#ef4444' },
    { code:'AUDIT',  title:'Compliance Audit Trail',                  color:'#3b82f6' },
    { code:'REGION', title:'Regional & Programme Access Control',     color:'#6366f1' },
    { code:'NOTICE', title:'Fleet Notice Board & Alerts',             color:'#ec4899' },
    { code:'DEPLOY', title:'Deployments & Driver Assignments',        color:'#14b8a6' },
    { code:'DISPOS', title:'Disposal Register & Approvals',           color:'#a855f7' },
];

export default function Login() {
    const history   = useHistory();
    const isMount   = useRef(true);
    const usernameRef = useRef();
    const passwordRef = useRef();

    const [values,           setValues]           = useState({});
    const [loading,          setLoading]          = useState(false);
    const [showPassword,     setShowPassword]     = useState(false);
    const [error,            setError]            = useState('');
    const [isTablet,         setIsTablet]         = useState(false);
    const [loginAttempts,    setLoginAttempts]    = useState(() => parseInt(localStorage.getItem('loginAttempts') || '0'));
    const [isLocked,         setIsLocked]         = useState(false);
    const [lockoutTime,      setLockoutTime]      = useState(() => {
        const saved = localStorage.getItem('lockoutTime');
        return saved ? parseInt(saved) : null;
    });
    const [rememberMe,       setRememberMe]       = useState(false);
    const [capsLockOn,       setCapsLockOn]       = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [connectionStatus, setConnectionStatus] = useState('online');

    const activityLogger = useActivityLogger({
        module: 'Login', events: ['click','submit','validation_error','lockout'],
        captureValues: false, logToConsole: process.env.NODE_ENV === 'development'
    });

    useEffect(() => {
        const checkDevice = () => {
            const w = window.innerWidth, h = window.innerHeight;
            setIsTablet(w >= 768 && w <= 1024);
        };
        checkDevice();
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    useEffect(() => {
        const upd = () => setConnectionStatus(navigator.onLine ? 'online' : 'offline');
        window.addEventListener('online', upd);
        window.addEventListener('offline', upd);
        return () => { window.removeEventListener('online', upd); window.removeEventListener('offline', upd); };
    }, []);

    useEffect(() => {
        const remembered = localStorage.getItem('rememberedUsername');
        if (remembered) { setValues(p => ({ ...p, username: remembered })); setRememberMe(true); }
        const lockout = localStorage.getItem('lockoutTime');
        if (lockout && Date.now() < parseInt(lockout)) {
            setIsLocked(true); setLockoutTime(parseInt(lockout));
        } else if (lockout) {
            localStorage.removeItem('lockoutTime');
            localStorage.removeItem('loginAttempts');
            setLoginAttempts(0);
        }
    }, []);

    useEffect(() => {
        if (isLocked && lockoutTime) {
            const t = setInterval(() => {
                if (Date.now() >= lockoutTime) {
                    setIsLocked(false); setLockoutTime(null); setLoginAttempts(0);
                    localStorage.removeItem('lockoutTime'); localStorage.removeItem('loginAttempts');
                }
            }, 1000);
            return () => clearInterval(t);
        }
    }, [isLocked, lockoutTime]);

    useEffect(() => { usernameRef.current?.focus(); }, []);
    useEffect(() => { return () => { isMount.current = false; }; }, []);

    const getRemainingTime = () => {
        if (!lockoutTime) return '';
        const rem = Math.ceil((lockoutTime - Date.now()) / 1000);
        if (rem <= 0) return '0:00';
        return `${Math.floor(rem/60)}:${(rem%60).toString().padStart(2,'0')}`;
    };

    const validateForm = () => {
        const errors = {};
        if (!values.username?.trim())        errors.username = 'Username is required';
        else if (values.username.length < 3) errors.username = 'At least 3 characters required';
        if (!values.password)                errors.password = 'Password is required';
        else if (values.password.length < 6) errors.password = 'At least 6 characters required';
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const login = async (e) => {
        if (e) e.preventDefault();
        if (connectionStatus === 'offline') { setError('No internet connection.'); return; }
        if (isLocked) { setError(`Account locked. Try again in ${getRemainingTime()}.`); return; }
        if (!validateForm()) { setError('Please fix the errors before continuing.'); return; }
        setError(''); setLoading(true);
        try {
            const { data } = await Auth.login(values);

            // ── Store auth data ───────────────────────────────────────────────
            util.setLoginInfoLocalStorage(
                data.token, data.name, data.modules, data.type,
                data.is_national, data.is_client_admin, data.is_admin
            );
            localStorage.setItem('is_admin',        data.is_admin        ? '1' : '0');
            localStorage.setItem('is_national',     data.is_national     ? '1' : '0');
            localStorage.setItem('is_client_admin', data.is_client_admin ? '1' : '0');

            const modulesCSV = Array.isArray(data.modules)
                ? data.modules.join(',')
                : (data.modules || '');
            localStorage.setItem('modules', modulesCSV);

            if (data.region)             localStorage.setItem('user_region',    data.region);
            else                         localStorage.removeItem('user_region');
            if (data.assigned_programme) localStorage.setItem('user_programme', data.assigned_programme);
            else                         localStorage.removeItem('user_programme');

            if (rememberMe) localStorage.setItem('rememberedUsername', values.username);
            else            localStorage.removeItem('rememberedUsername');

            setLoginAttempts(0);
            localStorage.removeItem('loginAttempts');
            localStorage.removeItem('lockoutTime');

            if (activityLogger?.logEvent) {
                activityLogger.logEvent('Login Successful', {
                    username: data.name, userType: data.type,
                    timestamp: new Date().toISOString(),
                    device: isTablet ? 'tablet' : 'desktop',
                });
            }

            // ── Redirect ─────────────────────────────────────────────────────
            const isAdmin       = !!data.is_admin;
            const isNational    = !!data.is_national;
            const isClientAdmin = !!data.is_client_admin;
            const isRegional    = !!(data.region && !data.is_admin && !data.is_national);
            const isProgramme   = !!data.assigned_programme;

            if (isAdmin || isNational || isClientAdmin || isRegional || isProgramme) {
                history.push('/fleet-notice-board');
            } else {
                history.push('/fleet-dashboard');
            }

        } catch (err) {
            const n = loginAttempts + 1;
            setLoginAttempts(n);
            localStorage.setItem('loginAttempts', n.toString());
            if (n >= 5) {
                const lockTime = Date.now() + 300000;
                setIsLocked(true); setLockoutTime(lockTime);
                localStorage.setItem('lockoutTime', lockTime.toString());
                setError('Too many failed attempts. Account locked for 5 minutes.');
            } else {
                setError(`${err.message || 'Invalid credentials'}. ${5-n} attempt${5-n!==1?'s':''} remaining.`);
                passwordRef.current?.focus();
                passwordRef.current?.select();
            }
        } finally {
            if (isMount.current) setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setValues(p => ({ ...p, [field]: value }));
        if (validationErrors[field]) setValidationErrors(p => ({ ...p, [field]: '' }));
        if (error) setError('');
    };

    const handleKeyDown = (e, field) => {
        if (field === 'password') setCapsLockOn(e.getModifierState?.('CapsLock') || false);
        if (e.key === 'Enter') {
            e.preventDefault();
            if (field === 'username') passwordRef.current?.focus();
            else login(e);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    const styles = `
        @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700;800&family=Crimson+Text:wght@400;600;700&display=swap');
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'EB Garamond', serif; }

        .ims-main {
            min-height:100vh;
            display:flex;
            align-items:stretch;
            position:relative;
            overflow:hidden;
        }

        /* ── LEFT PANEL ───────────────────────────── */
        .ims-left {
            width:48%;
            min-height:100vh;
            position:relative;
            overflow:hidden;
            background:#071220;
            display:flex;
            flex-direction:column;
        }
        .ims-left-bg {
            position:absolute;
            inset:0;
            background:url('https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=900&q=80&fit=crop&crop=center') center/cover no-repeat;
            filter:blur(4px) brightness(0.3);
            transform:scale(1.06);
        }
        .ims-left-overlay {
            position:absolute;
            inset:0;
            background:linear-gradient(160deg,rgba(7,18,32,0.55) 0%,rgba(7,18,32,0.82) 50%,rgba(7,18,32,0.96) 100%);
        }
        .ims-left-content {
            position:relative;
            z-index:3;
            flex:1;
            display:flex;
            flex-direction:column;
            justify-content:space-between;
            padding:2.8rem 3rem;
            min-height:100vh;
        }

        /* Top branding */
        .ims-left-top { display:flex; flex-direction:column; }
        .ims-moh-label {
            font-size:1rem;
            font-weight:800;
            letter-spacing:4px;
            text-transform:uppercase;
            color:#c9a961;
            margin-bottom:0.3rem;
        }
        .ims-kingdom {
            font-family:'Crimson Text', serif;
            font-size:1.3rem;
            font-weight:700;
            color:rgba(255,255,255,0.92);
            letter-spacing:1px;
        }

        /* Hero text */
        .ims-left-main {
            flex:1;
            display:flex;
            flex-direction:column;
            justify-content:center;
            padding:2rem 0;
        }
        .ims-left-title {
            font-size:4rem;
            font-weight:800;
            color:white;
            margin-bottom:0.6rem;
            text-shadow:0 3px 12px rgba(0,0,0,0.5);
            line-height:1.1;
        }
        .ims-left-title span { color:#c9a961; }
        .ims-left-subtitle {
            color:rgba(255,255,255,0.88);
            font-size:1.45rem;
            line-height:1.65;
            margin-bottom:2rem;
            text-shadow:0 1px 4px rgba(0,0,0,0.4);
        }
        .ims-badges { display:flex; gap:10px; flex-wrap:wrap; }
        .ims-badge {
            background:rgba(255,255,255,0.14);
            backdrop-filter:blur(12px);
            border:1.5px solid rgba(255,255,255,0.3);
            border-radius:24px;
            padding:6px 18px;
            color:white;
            font-size:0.9rem;
            font-weight:700;
            letter-spacing:0.5px;
        }

        /* Modules panel */
        .ims-modules {
            background:rgba(0,0,0,0.45);
            backdrop-filter:blur(16px);
            border:1px solid rgba(201,169,97,0.35);
            border-radius:12px;
            padding:1.4rem 1.8rem;
        }
        .ims-modules-title {
            font-size:0.85rem;
            font-weight:800;
            letter-spacing:3px;
            text-transform:uppercase;
            color:#c9a961;
            margin-bottom:1rem;
            border-bottom:1px solid rgba(201,169,97,0.25);
            padding-bottom:0.6rem;
        }
        .ims-module-item {
            display:flex;
            align-items:center;
            gap:12px;
            padding:6px 0;
            border-bottom:1px solid rgba(255,255,255,0.07);
        }
        .ims-module-item:last-child { border-bottom:none; }
        .ims-module-num {
            font-size:0.78rem;
            font-weight:700;
            color:rgba(255,255,255,0.4);
            width:18px;
            flex-shrink:0;
            text-align:right;
        }
        .ims-module-code {
            width:52px;
            height:24px;
            border-radius:5px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:0.65rem;
            font-weight:900;
            color:white;
            flex-shrink:0;
            letter-spacing:0.5px;
            box-shadow:0 2px 6px rgba(0,0,0,0.3);
        }
        .ims-module-title {
            font-size:0.84rem;
            color:rgba(255,255,255,0.88);
            line-height:1.3;
            font-weight:500;
        }

        /* ── RIGHT PANEL ───────────────────────────── */
        .ims-right {
            flex:1;
            background:#f8fafc;
            display:flex;
            align-items:center;
            justify-content:center;
            padding:3rem 2rem;
        }
        .ims-form-wrap { width:100%; max-width:460px; }

        /* Card */
        .ims-card {
            background:white;
            border:2px solid #c9a961;
            border-radius:8px;
            padding:2.4rem 2.2rem;
            box-shadow:0 0 0 8px rgba(201,169,97,0.08), 0 20px 60px rgba(0,0,0,0.12);
            position:relative;
        }

        /* Connection badge */
        .ims-conn {
            position:absolute;
            top:1rem; right:1rem;
            display:flex; align-items:center; gap:6px;
            padding:3px 10px;
            border-radius:20px;
            font-size:0.7rem;
            font-weight:700;
            letter-spacing:0.5px;
            text-transform:uppercase;
        }
        .ims-conn.online  { background:rgba(34,139,34,0.1); color:#228b22; border:1px solid #228b22; }
        .ims-conn.offline { background:rgba(178,34,34,0.1); color:#b22222; border:1px solid #b22222; }
        .ims-conn-dot { width:7px; height:7px; border-radius:50%; background:currentColor; }

        /* Card header */
        .ims-card-hdr {
            text-align:center;
            margin-bottom:1.8rem;
            padding-bottom:1.4rem;
            border-bottom:2px solid #c9a961;
        }
        .ims-emblem {
            width:88px; height:88px;
            margin:0 auto 1rem;
            border-radius:50%;
            display:flex; align-items:center; justify-content:center;
            box-shadow:0 0 0 6px rgba(201,169,97,0.2), 0 4px 16px rgba(0,0,0,0.12);
            background:white;
            overflow:hidden;
            padding:6px;
        }
        .ims-card-title {
            font-family:'Crimson Text', serif;
            font-size:1.75rem;
            font-weight:700;
            color:#0f2944;
            margin-bottom:0.15rem;
            letter-spacing:1px;
        }
        .ims-card-sub {
            font-size:0.78rem;
            color:#5a6c7d;
            font-weight:600;
            letter-spacing:0.5px;
            text-transform:uppercase;
        }

        /* Form fields */
        .ims-group { margin-bottom:1.2rem; }
        .ims-label {
            display:block;
            font-family:'Crimson Text', serif;
            font-size:0.85rem;
            font-weight:700;
            color:#0f2944;
            margin-bottom:0.4rem;
            letter-spacing:1.5px;
            text-transform:uppercase;
        }
        .ims-input-wrap { position:relative; }
        .ims-input {
            width:100%;
            padding:0.8rem 1rem;
            font-family:'EB Garamond', serif;
            font-size:1.1rem;
            font-weight:500;
            color:#0f2944;
            background:white;
            border:2px solid #c9a961;
            border-radius:4px;
            transition:all 0.2s;
            outline:none;
        }
        .ims-input::placeholder { color:#aaa; font-weight:400; }
        .ims-input:focus { border-color:#0f2944; box-shadow:0 0 0 3px rgba(15,41,68,0.08); }
        .ims-input.err { border-color:#b22222; background:#fff5f5; }
        .ims-pw-toggle {
            position:absolute;
            right:0.9rem; top:50%;
            transform:translateY(-50%);
            cursor:pointer;
            font-size:1.1rem;
            color:#888;
            user-select:none;
        }
        .ims-pw-toggle:hover { color:#0f2944; }
        .ims-field-err {
            color:#b22222;
            font-size:0.78rem;
            font-weight:600;
            margin-top:3px;
        }
        .ims-caps {
            background:#fff3cd;
            border:1px solid #ffc107;
            color:#856404;
            padding:4px 8px;
            border-radius:4px;
            font-size:0.75rem;
            font-weight:600;
            margin-top:4px;
        }

        /* Remember me */
        .ims-remember {
            display:flex; align-items:center; gap:8px;
            margin:1rem 0;
        }
        .ims-remember input { cursor:pointer; accent-color:#0f2944; }
        .ims-remember label { font-size:0.85rem; color:#5a6c7d; font-weight:500; cursor:pointer; }

        /* Alerts */
        .ims-alert-lock {
            background:linear-gradient(135deg,#fff8e1,#ffecb3);
            border:2px solid #ff8f00;
            border-radius:4px;
            padding:0.7rem;
            margin-bottom:1rem;
            color:#e65100;
            font-size:0.82rem;
            font-weight:600;
        }
        .ims-alert-err {
            background:linear-gradient(135deg,#fff5f5,#ffe8e8);
            border:2px solid #b22222;
            border-radius:4px;
            padding:0.7rem;
            margin-bottom:1rem;
            color:#b22222;
            font-size:0.82rem;
            font-weight:600;
        }
        .ims-attempts {
            background:#fff3cd;
            border:1px solid #ffc107;
            color:#856404;
            padding:6px 10px;
            border-radius:4px;
            font-size:0.76rem;
            font-weight:600;
            text-align:center;
            margin-bottom:0.8rem;
        }

        /* Submit button */
        .ims-btn {
            width:100%;
            padding:0.9rem 1.5rem;
            font-family:'Crimson Text', serif;
            font-size:1rem;
            font-weight:700;
            letter-spacing:2px;
            text-transform:uppercase;
            color:white;
            background:linear-gradient(135deg,#0f2944 0%,#1a3a52 50%,#0f2944 100%);
            border:2px solid #c9a961;
            border-radius:4px;
            cursor:pointer;
            transition:all 0.25s;
            box-shadow:0 4px 15px rgba(15,41,68,0.25);
        }
        .ims-btn:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 6px 20px rgba(15,41,68,0.35); }
        .ims-btn:active:not(:disabled) { transform:translateY(0); }
        .ims-btn:disabled { opacity:0.55; cursor:not-allowed; transform:none; }
        .ims-btn-inner { display:flex; align-items:center; justify-content:center; gap:10px; }
        .ims-spinner {
            width:16px; height:16px;
            border:3px solid rgba(255,255,255,0.3);
            border-top:3px solid white;
            border-radius:50%;
            animation:imspin 0.9s linear infinite;
        }
        @keyframes imspin { to { transform:rotate(360deg); } }

        /* Footer */
        .ims-card-footer {
            margin-top:1.4rem;
            padding-top:1.1rem;
            border-top:1px solid rgba(201,169,97,0.35);
            text-align:center;
            font-size:0.76rem;
            color:#888;
            font-weight:500;
        }

        /* Support link */
        .ims-support {
            margin-top:1rem;
            text-align:center;
            font-size:0.8rem;
            color:#5a6c7d;
        }
        .ims-support strong { color:#0f2944; }

        /* Responsive */
        @media (max-width:900px) {
            .ims-left { display:none; }
            .ims-right { padding:2rem 1.5rem; background:linear-gradient(160deg,#0f2944,#1a3a52); }
            .ims-form-wrap { max-width:100%; }
        }
        @media (prefers-reduced-motion:reduce) {
            *, *::before, *::after { animation-duration:0.01ms !important; transition-duration:0.01ms !important; }
        }
    `;

    return (
        <>
            <style>{styles}</style>
            <div className="ims-main">

                {/* ── LEFT PANEL ── */}
                <div className="ims-left">
                    <div className="ims-left-bg"/>
                    <div className="ims-left-overlay"/>
                    <div className="ims-left-content">

                        {/* MoH branding */}
                        <div className="ims-left-top">
                            <div className="ims-moh-label">Ministry of Health</div>
                            <div className="ims-kingdom">Kingdom of Eswatini</div>
                        </div>

                        {/* Hero text */}
                        <div className="ims-left-main">
                            <div className="ims-left-title">
                                Inventory &amp;<br/><span>Asset</span> Management
                            </div>
                            <div className="ims-left-subtitle">
                                Integrated system for tracking,<br/>managing and reporting on all<br/>MoH assets, fleet and resources.
                            </div>
                            <div className="ims-badges">
                                {['Fleet VMS','Assets','Inventory','Analytics'].map(v => (
                                    <span key={v} className="ims-badge">{v}</span>
                                ))}
                            </div>
                        </div>

                        {/* Modules panel */}
                        <div className="ims-modules">
                            <div className="ims-modules-title">System Modules</div>
                            {IMS_MODULES.map((m, i) => (
                                <div key={m.code} className="ims-module-item">
                                    <span className="ims-module-num">{i+1}.</span>
                                    <div className="ims-module-code" style={{ background: m.color }}>
                                        {m.code}
                                    </div>
                                    <span className="ims-module-title">{m.title}</span>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>

                {/* ── RIGHT PANEL ── */}
                <div className="ims-right">
                    <div className="ims-form-wrap">
                        <div className="ims-card">

                            {/* Connection status */}
                            <div className={`ims-conn ${connectionStatus}`}>
                                <span className="ims-conn-dot"/>
                                <span>{connectionStatus === 'online' ? 'Connected' : 'Offline'}</span>
                            </div>

                            {/* Header */}
                            <div className="ims-card-hdr">
                                <div className="ims-emblem">
                                    <img src="assets/img/elogo.png" alt="Eswatini Coat of Arms"
                                        style={{ width:'100%', height:'100%', objectFit:'contain' }}
                                        onError={e => { e.target.style.display='none'; }}
                                    />
                                </div>
                                <div className="ims-card-title">Authorised Access</div>
                                <div className="ims-card-sub">Inventory Management System</div>
                            </div>

                            <form onSubmit={login} autoComplete="off">

                                {/* Username */}
                                <div className="ims-group">
                                    <label className="ims-label">Username</label>
                                    <div className="ims-input-wrap">
                                        <input
                                            ref={usernameRef}
                                            type="text"
                                            readOnly
                                            onFocus={e => e.target.removeAttribute('readOnly')}
                                            placeholder="Enter your username"
                                            value={values.username || ''}
                                            onChange={e => handleInputChange('username', e.target.value)}
                                            onKeyDown={e => handleKeyDown(e, 'username')}
                                            className={`ims-input${validationErrors.username ? ' err' : ''}`}
                                            autoComplete="off"
                                            disabled={isLocked}
                                        />
                                    </div>
                                    {validationErrors.username && (
                                        <div className="ims-field-err">⚠ {validationErrors.username}</div>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="ims-group">
                                    <label className="ims-label">Password</label>
                                    <div className="ims-input-wrap">
                                        <input
                                            ref={passwordRef}
                                            readOnly
                                            onFocus={e => e.target.removeAttribute('readOnly')}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter your password"
                                            value={values.password || ''}
                                            onChange={e => handleInputChange('password', e.target.value)}
                                            onKeyDown={e => handleKeyDown(e, 'password')}
                                            className={`ims-input${validationErrors.password ? ' err' : ''}`}
                                            autoComplete="new-password"
                                            disabled={isLocked}
                                        />
                                        <div className="ims-pw-toggle"
                                            onClick={() => setShowPassword(s => !s)}
                                            title={showPassword ? 'Hide password' : 'Show password'}>
                                            {showPassword ? '👁️' : '👁️‍🗨️'}
                                        </div>
                                    </div>
                                    {validationErrors.password && (
                                        <div className="ims-field-err">⚠ {validationErrors.password}</div>
                                    )}
                                    {capsLockOn && (
                                        <div className="ims-caps">⚠ Caps Lock is ON</div>
                                    )}
                                </div>

                                {/* Remember me */}
                                <div className="ims-remember">
                                    <input type="checkbox" id="ims-rem"
                                        checked={rememberMe}
                                        onChange={e => setRememberMe(e.target.checked)}
                                        disabled={isLocked}
                                    />
                                    <label htmlFor="ims-rem">Remember my username</label>
                                </div>

                                {/* Lockout */}
                                {isLocked && (
                                    <div className="ims-alert-lock">
                                        🔒 Account temporarily locked. Try again in {getRemainingTime()}
                                    </div>
                                )}

                                {/* Error */}
                                {error && !isLocked && (
                                    <div className="ims-alert-err">⛔ {error}</div>
                                )}

                                {/* Attempts warning */}
                                {loginAttempts > 0 && loginAttempts < 5 && !isLocked && (
                                    <div className="ims-attempts">
                                        <strong>Security:</strong> {5 - loginAttempts} attempt{5-loginAttempts!==1?'s':''} remaining before lockout
                                    </div>
                                )}

                                {/* Submit */}
                                <button type="submit" className="ims-btn"
                                    disabled={loading || isLocked || !values.username || !values.password || connectionStatus === 'offline'}>
                                    {loading ? (
                                        <div className="ims-btn-inner">
                                            <div className="ims-spinner"/>
                                            <span>Authenticating...</span>
                                        </div>
                                    ) : connectionStatus === 'offline' ? (
                                        <div className="ims-btn-inner"><span>No Connection</span></div>
                                    ) : isLocked ? (
                                        <div className="ims-btn-inner"><span>🔒 Account Locked</span></div>
                                    ) : (
                                        <div className="ims-btn-inner"><span>Sign In</span></div>
                                    )}
                                </button>

                            </form>

                            {/* Support */}
                            <div className="ims-support">
                                🆘 Technical Support &nbsp;·&nbsp; <strong>📞 +2404 7712</strong>
                            </div>

                            <div className="ims-card-footer">
                                For official use only &nbsp;·&nbsp;
                                © {new Date().getFullYear()} Kingdom of Eswatini — Ministry of Health
                            </div>

                        </div>
                    </div>
                </div>

            </div>
        </>
    );
}